// src/data/claimReturn.js
//
// RETURNING A CLAIM TO ITS AUTHOR  -  ruling 72, which amends 61.6.
//
// -------------------------------------------------------------------------------------------
// THE HISTORY MATTERS HERE MORE THAN USUAL, BECAUSE THIS FEATURE HAS BEEN DECIDED THREE TIMES.
//
//   67 (ex-63), 2026-08-28 19:15Z   an LC MAY return a claim to draft, with guards
//   61.6,       2026-08-28 21:15Z   overruled it. "submitted -> draft is NOT a legal move."
//   72,         2026-08-29          amends 61.6. The return exists again, and this module is it.
//
// 61.6's text is NOT edited and 72 does not pretend it was. See data\YCP_RULINGS_2026-08-29.md.
//
// WHAT CHANGED BOB'S MIND, AND IT IS WHY THIS MODULE IS SHAPED THE WAY IT IS: 67.3 killed the
// return by finding that a returned claim becomes INVISIBLE to the officer who returned it - a
// returned claim is a draft, and ruling 56 puts a draft beyond everyone but its author. Bob's answer
// was to point ruling 27c DOWNWARD: "the receiving office gets BOTH: the prior holder's frozen copy,
// read-only, and its own fresh working copy." The member gets a fresh working copy; the officer
// keeps the frozen snapshot and a content-free queue entry. He is not blinded.
//
// -------------------------------------------------------------------------------------------
// 67.2's TWO HAZARDS ARE NOT ANSWERED BY VERSIONING AND ARE ANSWERED HERE INSTEAD.
//
//   TIMING             "A claim sent back to draft keeps burning that window while it sits in a
//                      state nobody but its author can see. The return does not pause the clock and
//                      must never appear to."
//                      -> the window NEVER recomputes; the claim stays in the officer's queue with
//                         its clock counting; a return inside the last stretch warns hard and NAMES
//                         THE DATE rather than proceeding quietly.
//
//   MULTIPLE HANDLING  "Every hop is a chance for the claim to be edited by someone with less of
//                      the picture, to lose an attachment, to be re-submitted with a changed date,
//                      or to stall because each party believes the other holds it."
//                      -> every return increments `returnCount`, so repeated bouncing is VISIBLE
//                         rather than a thing nobody counted; and the officer's queue entry answers
//                         "who holds it" so neither party can assume the other does.
// -------------------------------------------------------------------------------------------

import { daysUntilISO, isReturned } from './deskQueue';
import { stageIndex, ELEVATION_STAGE } from './claimLadder';

// Below this many days of filing window remaining, a return WARNS HARD and names the date.
// TEN DAYS IS THE ASSISTANT'S CHOICE AND NOT BOB'S RULING - it matches WINDOW_URGENT_DAYS in
// deskQueue.js deliberately, so the desk and the return speak about the same horizon rather than
// two. One constant; overturning it is a one-line edit.
export const RETURN_WARN_DAYS = 10;

// A reason is REQUIRED. 72.3 item 1: the return is an event, recorded, with a reason.
// Same shape and the same rationale as the outcome reason at 61.9a - short, never pre-filled, so a
// blank is visibly a blank rather than a default nobody read.
export const REASON_MIN = 4;
export const REASON_MAX = 400;

// WHO MAY RETURN. Ruling 72.1: a Local Chairperson or a General Chairperson.
//
// THE ADMINISTRATOR IS DELIBERATELY EXCLUDED, and it is not an oversight: ruling 66 gives him READ
// on a draft and nothing else. A return CREATES a draft. An administrator manufacturing a document
// he may then only read is a contradiction, and it would put a member's claim into a state its
// author did not ask for, on the authority of somebody with no representational role in it.
export function mayReturn(claim, user) {
  if (!claim || !user) return false;
  const i = stageIndex(claim.status);
  if (i < 0) return false;                                     // off-ladder: nothing to return
  if (String(claim.status || '') === 'draft') return false;     // already a draft
  if (user.role === 'general_chair') {
    return !!user.committee && user.committee === claim.committee;
  }
  if (user.role === 'local_chair') {
    const inScope = Array.isArray(user.scopes) && user.scopes.indexOf(claim.scopeKey) !== -1;
    // 27c: at or past gc_review the matter has moved up and he no longer holds it, so he cannot
    // send it anywhere - including back.
    return inScope && i < stageIndex(ELEVATION_STAGE);
  }
  return false;
}

// Days of filing window left, or null when the platform has none on record. `expiresAt` is written
// only where the agreement carries a filing-window profile AND a date of occurrence exists, so null
// is common and must stay null - an unknown clock is never rendered as a comfortable one.
export function windowLeft(claim, nowMs) {
  return daysUntilISO(claim && claim.expiresAt, nowMs);
}

// THE WARNING. Returned as data rather than thrown, because the officer may proceed - 72.3 item 5
// says a return inside the last stretch WARNS HARD AND NAMES THE DATE. It does not forbid.
//
// Three distinct states, and they are not merged:
//   passed    the window has already run out
//   closing   inside RETURN_WARN_DAYS
//   unknown   no window on record - which is NOT "plenty of time"
export function returnWarning(claim, opts) {
  const o = opts || {};
  const left = windowLeft(claim, o.nowMs);
  const date = String((claim && claim.expiresAt) || '');
  const limit = typeof o.warnDays === 'number' ? o.warnDays : RETURN_WARN_DAYS;

  if (left === null) {
    return {
      level: 'unknown',
      text: 'This claim has NO filing window on record, so nothing here can tell you how long the '
          + 'author has. That is not the same as plenty of time. The clock does not pause because '
          + 'you returned it.',
    };
  }
  if (left < 0) {
    return {
      level: 'passed',
      text: 'The filing window PASSED on ' + date + ', ' + Math.abs(left) + ' day(s) ago. Returning '
          + 'this claim does not restart it and never will.',
    };
  }
  if (left <= limit) {
    return {
      level: 'closing',
      text: 'The filing window closes on ' + date + ' - ' + left + ' day(s) from now. It keeps '
          + 'running while the claim sits with its author, where you cannot see or work it. If he '
          + 'does not act, it closes. Consider asking him a question instead of handing it back.',
    };
  }
  return { level: 'ok', text: '' };
}

// EVERYTHING THE RETURN WRITES, as a pure function, so the payload is under test rather than
// assembled in a click handler.
//
// Returns { ok, errors[], warning, patch } - never a partial patch.
//
// WHAT IS DELIBERATELY ABSENT FROM THE PATCH, and each absence is a ruling:
//   expiresAt        72.3 item 6 - THE WINDOW IS NEVER RECOMPUTED. Anchored to the date of
//                    occurrence. Its absence here is the guarantee.
//   scopeKey /
//   committee        jurisdiction does not move because a claim went back to its author.
//   createdBy /
//   createdAt        coreIntact() in firestore.rules requires both unchanged.
//   outcome*         a returned claim has not ended.
export function returnPatch(claim, user, input) {
  const i = input || {};
  const errors = [];

  if (!mayReturn(claim, user)) {
    const atCeiling = claim && stageIndex(claim.status) >= stageIndex(ELEVATION_STAGE)
                   && user && user.role === 'local_chair';
    errors.push(atCeiling
      ? 'You elevated this claim, so under ruling 27c it has moved up and you keep read, not edit. '
      + 'You cannot send it back from here - raise it with your General Chairperson.'
      : 'This claim is not yours to return.');
  }

  const reason = String(i.reason == null ? '' : i.reason).trim();
  if (!reason) {
    errors.push('A reason is required. The author is being handed his own claim back and is entitled '
              + 'to know what for. [R-72.3]');
  } else if (reason.length < REASON_MIN) {
    errors.push('That is too short to be a reason (' + reason.length + ' characters). Say what he '
              + 'needs to do.');
  } else if (reason.length > REASON_MAX) {
    errors.push('Keep it short - ' + REASON_MAX + ' characters at most. Detail belongs in a note.');
  }

  const warning = returnWarning(claim, i);

  if (errors.length) return { ok: false, errors, warning, patch: null };

  const prior = Number(claim && claim.returnCount);
  const patch = {
    status: 'draft',
    returnedAt: i.at || new Date().toISOString(),
    returnedBy: (user && (user.name || user.email)) || 'Unknown',
    returnedByRole: (user && user.role) || 'unknown',
    returnedToUid: String((claim && claim.createdBy) || ''),
    returnReason: reason,
    // 67.2's multiple-handling hazard, made countable. A claim on its fourth trip is a fact an
    // officer should be able to see without reading the whole note history.
    returnCount: (isFinite(prior) && prior > 0 ? prior : 0) + 1,
    // The window at the moment it went back, so the record shows what the officer knew when he
    // did it. NOT a new deadline - a snapshot of the old one.
    returnedWithWindowDays: windowLeft(claim, i.nowMs),
  };

  return { ok: true, errors: [], warning, patch };
}

// ONE DEFINITION, AND IT LIVES IN deskQueue.js. The officer's desk needs this predicate to build the
// "Returned to you" queue, and this module already imports daysUntilISO from there - so defining it
// here as well would either duplicate it or create an import cycle. It is re-exported so every
// existing import of `isReturned` from this module keeps working, the test suite included.
export { isReturned };

// THE OFFICER'S QUEUE ENTRY  -  72.3 item 4, and the whole reason ruling 72 is buildable at all.
//
// CONTENT-FREE, AND THAT IS THE POINT. Ruling 56 puts a draft beyond everyone but its author, and a
// returned claim IS a draft. This is NOT a read of the draft: it is a record of the RETURN, which is
// the officer's own act. He sees that it exists, its number, who has it, when it went back, why he
// sent it, and the clock. He sees NO content, no title, no narrative, no documents.
//
// If this function ever grows a field carrying the member's words, ruling 56 has been breached by
// a convenience.
// SECTION 488: THE ENTRY, PERSISTED. returnedQueueEntry() above the fold of this function describes what the
// officer keeps; returnRecord() is the stored form of it, written in the same batch as the return itself to
// claimReturns/{claimId}. It carries the routing keys the rules need (scopeKey, committee), the officer's
// uid (the rules require it to be the writer), and NOTHING the member wrote. firestore.rules pins every key.
//
// RECORD 588 (ruling 202 audit): "the claimant is told". When the named claimant is NOT the author (an officer or a
// colleague filed for him), he cannot read the draft (ruling 56) and was never told it went back. The record now
// carries claimantUid and claimantEmail, copied from the claim, ONLY in that case; firestore.rules checks both
// against the claim and lets that member read this content-free record (number, date, reason, who) - nothing more.
export const RETURN_RECORD_CLAIMANT_KEYS = ['claimantUid', 'claimantEmail'];
export const RETURN_RECORD_KEYS = ['claimId', 'claimNumber', 'scopeKey', 'committee', 'returnedAt', 'returnedBy',
  'returnedByUid', 'returnedToUid', 'returnReason', 'returnCount', 'windowDate', 'heldBy'].concat(RETURN_RECORD_CLAIMANT_KEYS);
// The claimant is someone other than the author: by uid where the claim carries one, else by e-mail against the
// filer's (filedBy). Unknown either way -> not different; the author already holds the draft.
export function claimantIsNotAuthor(claim) {
  const c = claim || {};
  const uid = String(c.claimantUid || '');
  if (uid) return uid !== String(c.createdBy || '');
  const em = String(c.claimantEmail || '').trim().toLowerCase();
  return !!em && em !== String(c.filedBy || '').trim().toLowerCase();
}
export function returnRecord(claim, patch, user, claimId) {
  const c = claim || {};
  const p = patch || {};
  const rec = {
    claimId: String(claimId || c.docId || c.id || ''),
    claimNumber: c.id && c.id !== claimId ? String(c.id) : null,
    scopeKey: c.scopeKey || null,
    committee: c.committee || null,
    returnedAt: p.returnedAt || null,
    returnedBy: p.returnedBy || null,
    returnedByUid: (user && user.uid) || null,
    returnedToUid: p.returnedToUid || null,
    returnReason: p.returnReason || null,         // the OFFICER's words
    returnCount: Number(p.returnCount) || 1,
    windowDate: c.expiresAt || null,               // never recomputed (67.2)
    heldBy: 'author',
  };
  // Copied exactly as the claim holds them (the rule compares with c.get(key, null)).
  if (claimantIsNotAuthor(c)) {
    rec.claimantUid = c.claimantUid == null ? null : String(c.claimantUid);
    rec.claimantEmail = c.claimantEmail == null ? null : String(c.claimantEmail);
  }
  return rec;
}

// The desk rows for returned claims the officer can no longer read. `visibleIds` are the claims his desk
// already holds: a claim that has come back from its author (re-filed) is visible again and is not listed
// twice. Each row is shaped like a claim for deskQueue's partition - status 'draft' and returnedAt - and
// is marked `returnOnly` so the page does not offer to open what the officer may not read.
export function returnRowsForDesk(records, visibleIds) {
  const seen = new Set((visibleIds || []).map(String));
  return (records || [])
    .filter(r => r && r.claimId && !seen.has(String(r.claimId)))
    .map(r => ({
      docId: r.claimId, id: r.claimNumber || r.claimId, status: 'draft',
      returnedAt: r.returnedAt, returnedBy: r.returnedBy, returnReason: r.returnReason,
      returnCount: r.returnCount, windowDate: r.windowDate || null,   // read by deskQueue; expiresAt stays computeExpiresAt()'s alone (WIN-1)
      scopeKey: r.scopeKey, committee: r.committee, returnOnly: true,
    }));
}

export function returnedQueueEntry(claim, opts) {
  if (!isReturned(claim)) return null;
  const left = windowLeft(claim, (opts || {}).nowMs);
  return {
    claimId: claim.id || claim.docId || null,
    returnedAt: claim.returnedAt || null,
    returnedBy: claim.returnedBy || null,
    returnReason: claim.returnReason || null,      // the OFFICER's own words, not the member's
    returnCount: Number(claim.returnCount) || 1,
    windowDaysLeft: left,                          // null means no window on record, never "safe"
    windowDate: claim.expiresAt || null,
    heldBy: 'author',
  };
}

// ===========================================================================================
// RULING 72.3 ITEMS 2 AND 7 (Record 583, mandate audit item 9) - THE FROZEN VERSION AND THE MARKS.
//
// 72.3 item 2: "A frozen version is taken at the return (27c, 61.3), marked per ISS-46 section 46.7: SUPERSEDED,
// never VOID - it was operative when it was served." Until Record 583 no version was taken: the officer lost the
// claim entirely, which is the blinding 67.3 found and 72.2 answered. The version is written in the SAME batch
// as the return, to claims/{id}/versions/r{returnCount}; firestore.rules REFUSES a return without it and
// requires `snapshot` to equal the claim exactly as it stood before the batch - the copy cannot be edited.
// 72.3 item 7: "The returned draft is marked INCOMPLETE" - returnMark() below, shown to the author.
export const FROZEN_MARK = 'SUPERSEDED';
export const RETURNED_DRAFT_MARK = 'INCOMPLETE';
export const FROZEN_VERSION_KEYS = ['kind', 'marking', 'returnCount', 'frozenAt', 'frozenByUid', 'scopeKey',
  'committee', 'authorUid', 'statusAtFreeze', 'snapshot'];
export function frozenVersionId(returnCount) { return 'r' + String(Number(returnCount) > 0 ? Number(returnCount) : 1); }

// claimData: the claim document's data EXACTLY as read (no id merged in). at: serverTimestamp() from the caller -
// this module stays free of Firestore so the suite can run it.
export function frozenVersion(claimData, patch, user, at) {
  const c = claimData || {};
  const has = (k) => Object.prototype.hasOwnProperty.call(c, k);
  return {
    kind: 'return',
    marking: FROZEN_MARK,
    returnCount: Number((patch || {}).returnCount) || 1,
    frozenAt: at,
    frozenByUid: (user && user.uid) || null,
    scopeKey: has('scopeKey') ? c.scopeKey : null,
    committee: has('committee') ? c.committee : null,
    authorUid: has('createdBy') ? c.createdBy : null,
    statusAtFreeze: c.status,
    snapshot: c,
  };
}

// What the officer's read-only view lists: every field of the frozen copy as text, in a fixed order.
export function frozenFields(snapshot) {
  const s = snapshot || {};
  const show = (v) => {
    if (v === null || v === undefined) return '';
    if (v && typeof v.toDate === 'function') return v.toDate().toISOString();
    if (Array.isArray(v)) return v.map(x => (x && typeof x === 'object') ? (x.text || JSON.stringify(x)) : String(x)).join('\n');
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
  };
  return Object.keys(s).sort().map(k => [k, show(s[k])]).filter(([, v]) => v !== '');
}

// 72.3 item 7. Null unless the claim is a returned draft. The reason rides on the stamp (46.7).
export function returnMark(claim) {
  if (!isReturned(claim)) return null;
  const c = claim || {};
  const when = String(c.returnedAt || '').slice(0, 10);
  const win = c.expiresAt ? ' The filing window closes on ' + c.expiresAt + ' and it keeps running.'
                          : ' No filing window is on record - that is not the same as plenty of time.';
  return {
    mark: RETURNED_DRAFT_MARK,
    text: 'Returned to you' + (c.returnedBy ? ' by ' + c.returnedBy : '') + (when ? ' on ' + when : '')
        + (c.returnReason ? ' - a/c ' + c.returnReason : '') + '. Fix it and file it again.' + win,
  };
}
